import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPagedResult } from '../../shared/models/paged-result';
import { ICarrier } from '../../shared/models/despatch/carrier';
import { ICarrierFilterSet } from '../../shared/models/despatch/carrier-filter-set';
import { serialize } from '../../utility/serialize';
import { IDriver } from '../../shared/models/despatch/driver';
import { IDriverFilterSet } from '../../shared/models/despatch/driver-filter-set';
import { IVehicle } from '../../shared/models/despatch/vehicle';
import { IVehicleFilterSet } from '../../shared/models/despatch/vehicle-filter-set';
import { BaseFilterSet } from '../../shared/models/base-filter-set';
import { ITransferReason } from '../../shared/models/despatch/transfer-reason';
import { ITransportModality } from '../../shared/models/despatch/transport-modality';
import { ITransferReasonFilterSet } from '../../shared/models/despatch/transfer-reason-filter-set';
import { ICancellationReason } from '../../shared/models/despatch/cancellation-reason';
import { ICancellationDespatch } from '../../shared/models/despatch/cancellation-despatch';

export const despatchAdviceApi = createApi({
  reducerPath: 'despatchAdviceApi',
  baseQuery,
  endpoints: (builder) => ({
    printDespatchAdvicePdf: builder.query<Blob, { id: string }>({
      query: ({ id }) => {
        const randomParam = Date.now();
        const url = `/api/despatchAdvices/${id}/views/pdf?_=${randomParam}`;
        return {
          url,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    getCarriers: builder.query<IPagedResult<ICarrier>, ICarrierFilterSet>({
      query: (filterSet) => ({
        url: `/api/carriers?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    getDrivers: builder.query<IPagedResult<IDriver>, IDriverFilterSet>({
      query: (filterSet) => ({
        url: `/api/drivers?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    getVehicles: builder.query<IPagedResult<IVehicle>, IVehicleFilterSet>({
      query: (filterSet) => ({
        url: `/api/vehicles?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    getTransferReasons: builder.query<IPagedResult<ITransferReason>, ITransferReasonFilterSet>({
      query: (filterSet) => ({
        url: `/api/transferReasons?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    getTransportModalities: builder.query<IPagedResult<ITransportModality>, BaseFilterSet>({
      query: (filterSet) => ({
        url: `/api/transportModalities?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    getCancellationreasons: builder.query<IPagedResult<ICancellationReason>, BaseFilterSet>({
      query: (filterSet) => ({
        url: `/api/cancellationReasons?${serialize(filterSet)}`,
        method: 'GET',
      }),
    }),
    cancellationDespatch: builder.mutation<string, ICancellationDespatch>({
      query: (body) => ({
        url: `/api/despatchAdvices/${body.despatchAdviceId}/status`,
        method: 'PUT',
        body,
      }),
    }),
    printCancelDespatchAdvicePdf: builder.query<Blob, { id: string }>({
      query: ({ id }) => {
        const randomParam = Date.now();
        const url = `/api/despatchAdvices/cancel/${id}/views/pdf?_=${randomParam}`;
        return {
          url,
          method: 'GET',
          responseHandler: (response) => response.blob(),
        };
      },
    }),
  }),
});

export const {
  useLazyPrintDespatchAdvicePdfQuery,
  useLazyGetCarriersQuery,
  useLazyGetDriversQuery,
  useLazyGetVehiclesQuery,
  useLazyGetTransferReasonsQuery,
  useLazyGetTransportModalitiesQuery,
  useGetCancellationreasonsQuery,
  useCancellationDespatchMutation,
  useLazyGetCancellationreasonsQuery,
  useLazyPrintCancelDespatchAdvicePdfQuery,
} = despatchAdviceApi;
